import { generateCSSBreakpointMapForImages } from "@faire/deprecated-retailer-visitor-shared/components/LayoutElementRenderer/__internal__/Banner/ByType/utils";
import { Context } from "@faire/deprecated-retailer-visitor-shared/components/LayoutElementRenderer/__internal__/Context";
import { getMilestoneInteractionsByType } from "@faire/deprecated-retailer-visitor-shared/components/LayoutElementRenderer/__internal__/Utils";
import { EAEAEA_PIXEL } from "@faire/deprecated-retailer-visitor-shared/consts/EAEAEA_PIXEL";
import { useRecordMilestone } from "@faire/deprecated-retailer-visitor-shared/stores/domain/useMilestone";
import { StyledIconByName } from "@faire/deprecated-retailer-visitor-shared/views/product/utils/StyledIconByName";
import { Core, Decorative } from "@faire/design-tokens";
import { IIconTextListItem } from "@faire/web-api--source/indigofair/data/IIconTextListItem";
import { ILayoutElementData } from "@faire/web-api--source/indigofair/data/ILayoutElementData";
import {
  optimizeImageUrl,
  optimizeImageUrlHeight,
} from "@faire/web/common/images/optimizeImages";
import { useIsSSR } from "@faire/web/common/server-side-rendering/isSSR";
import { isNotUndefined } from "@faire/web/common/typescriptUtils";
import { Button } from "@faire/web/slate/Button";
import { Close } from "@faire/web/slate/icons/Close";
import { Column, Flex, Row } from "@faire/web/slate/Layout";
import { getSpacing, Spacer } from "@faire/web/slate/spacing";
import { responsiveMediaQueries } from "@faire/web/slate/Theme/responsiveMediaQueries";
import { Typography } from "@faire/web/slate/Typography";
import { useDoActionWhenVisible } from "@faire/web/ui/hooks/useDoActionWhenVisible";
import { useResponsiveValue } from "@faire/web/ui/hooks/useResponsiveValue";
import { useViewport } from "@faire/web/ui/hooks/useViewport";
import isEmpty from "lodash/isEmpty";
import React, { useState } from "react";
import styled from "styled-components";

const DESKTOP_IMAGE_WIDTH = 584;
const MOBILE_IMAGE_HEIGHT = 160;

interface IListItemProps {
  listItem: IIconTextListItem;
}

const ListItem: React.FC<IListItemProps> = ({ listItem }) => {
  return (
    <Row>
      <IconWrapper>
        <StyledIconByName
          name={listItem.icon?.type}
          fontSize="16px"
          color={Core.icon.default}
          strokeWidth={1}
        />
      </IconWrapper>
      <Typography>{listItem.description_text}</Typography>
    </Row>
  );
};

export const MarketingBanner: React.FC = () => {
  const { layoutElement, onCTAClick, requestId } = React.useContext(Context);
  const isSSR = useIsSSR();
  const { isTabletAndAbove } = useViewport();

  const [isClosed, setIsClosed] = useState<boolean>(false);

  const recordMilestone = useRecordMilestone();
  const onViewRef = useDoActionWhenVisible(() => {
    getMilestoneInteractionsByType(layoutElement, "RECORD_UPON_SEEN").forEach(
      (milestone) => {
        recordMilestone(milestone);
      }
    );
  });

  const images = generateCSSBreakpointMapForImages(layoutElement.images);
  const backgroundImage = useResponsiveValue({
    mobileAndAbove: {
      $backgroundImageUrl: (images.mobile ?? layoutElement.background_image)
        ?.url,
      $height: MOBILE_IMAGE_HEIGHT,
    },
    tabletAndAbove: {
      $backgroundImageUrl: (images.tablet ?? layoutElement.background_image)
        ?.url,
      $width: DESKTOP_IMAGE_WIDTH,
    },
  });

  if (isClosed) {
    return null;
  }

  const ecoFriendlyIconTextListItems: IIconTextListItem[] =
    layoutElement.layout_element_data
      ?.map((data: ILayoutElementData) => {
        return data?.icon_text_list_item;
      })
      .filter(isNotUndefined);

  const uponDismissMilestones = getMilestoneInteractionsByType(
    layoutElement,
    "RECORD_UPON_DISMISS"
  );
  const shouldShowCloseButton = uponDismissMilestones.length > 0;

  const onCloseClick = () => {
    setIsClosed(true);

    uponDismissMilestones.forEach((milestone) => {
      recordMilestone(milestone);
    });
  };

  const onCTAButtonClick = () => {
    getMilestoneInteractionsByType(layoutElement, "RECORD_UPON_CLICK").forEach(
      (m) => {
        recordMilestone(m);
      }
    );

    onCTAClick?.(
      layoutElement.on_click_event_name ?? "MarketingBannerClick",
      requestId
    );
  };

  return (
    <Wrapper ref={onViewRef}>
      <BannerImage
        data-test-id="marketing-banner-image"
        $isMobile={!isTabletAndAbove}
        $isSSR={isSSR}
        {...backgroundImage}
      />
      <Content>
        {layoutElement.tagline_text ? (
          <>
            <Typography variant="paragraphSansMedium">
              {layoutElement.tagline_text}
            </Typography>
            <Spacer height="2x" />
          </>
        ) : null}
        <Typography variant="displaySSerifRegular">
          {layoutElement.main_text}
        </Typography>
        <Spacer height="2x" />
        <Typography variant="paragraphSansRegular">
          {layoutElement.description_text}
        </Typography>
        {isTabletAndAbove && !isEmpty(ecoFriendlyIconTextListItems) ? (
          <>
            <Spacer height="2x" />
            {ecoFriendlyIconTextListItems.map(
              (listItem: IIconTextListItem, index: number) => {
                return (
                  <ListItem
                    key={index + `${listItem.slate_icon}`}
                    listItem={listItem}
                  />
                );
              }
            )}
          </>
        ) : null}
        <Spacer height="2x" />
        <CTAButton linkTo={layoutElement.cta_link} onClick={onCTAButtonClick}>
          {layoutElement.cta_text}
        </CTAButton>
      </Content>
      {shouldShowCloseButton ? (
        <CloseIconButton data-test-id="close-button" onClick={onCloseClick}>
          <Close fontSize="12px" color={Core.icon.subdued} />
        </CloseIconButton>
      ) : null}
    </Wrapper>
  );
};

const Wrapper = styled(Flex).attrs({
  direction: { mobileAndAbove: "column", tabletAndAbove: "row-reverse" },
})`
  overflow: hidden;
  position: relative;
  ${responsiveMediaQueries.mobile} {
    margin: ${getSpacing("3x", 0, "2x")};
  }
`;

const Content = styled(Column)`
  background-color: ${Decorative.tint.neutral};
  width: 100%;
  padding: ${getSpacing("4x")};
  ${responsiveMediaQueries.tabletAndAbove} {
    width: 50%;
    padding: ${getSpacing("6x")};
  }
`;

interface BackGroupImageProps {
  $isMobile: boolean;
  $isSSR: boolean;
  $backgroundImageUrl?: string;
  $width?: number;
  $height?: number;
}

const getBackgroundUrl = ({
  $isMobile,
  $isSSR,
  $backgroundImageUrl = EAEAEA_PIXEL,
  $width,
  $height,
}: BackGroupImageProps) => {
  if ($isMobile && $height) {
    return optimizeImageUrlHeight(
      { url: $backgroundImageUrl },
      $isSSR,
      $height
    );
  } else if ($width) {
    return optimizeImageUrl({ url: $backgroundImageUrl }, $isSSR, {
      width: $width,
    });
  }

  return undefined;
};

const BannerImage = styled.div<BackGroupImageProps>`
  display: ${(getBackgroundUrl) =>
    !getBackgroundUrl.$backgroundImageUrl ? "none" : undefined};
  flex-shrink: 0;
  width: 100%;
  height: ${MOBILE_IMAGE_HEIGHT}px;
  background-image: url(${getBackgroundUrl});
  background-size: cover;
  background-position: center;

  ${responsiveMediaQueries.tabletAndAbove} {
    width: 50%;
    height: auto;
  }
`;

const CTAButton = styled(Button)`
  width: 100%;
  position: unset;
  ${responsiveMediaQueries.tabletAndAbove} {
    width: fit-content;
  }

  &:before {
    content: " ";
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
  }
`;

const IconWrapper = styled(Column)`
  margin: 2px 12px 0 0;
`;

const CloseIconButton = styled.div`
  cursor: pointer;
  position: absolute;
  right: ${getSpacing("1x")};
  top: ${getSpacing("1x")};
`;
